// CourseRow — THE single course-row treatment used everywhere on the Moduler
// page (#74): program modules, the "Fler moduler" catalog, and completed history
// all render through this one component, so the page speaks one design language.
// Built on the app's native tokens (NEXILINK_ROW, CourseAvatar, StatusBadge,
// #1A4D4F/#A4C3B2 brand palette) — no bespoke styling.
import React from 'react';
import { ChevronRight, Clock, Layers, Lock, Play } from 'lucide-react';
import { CourseAvatar } from './CourseAvatar';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import type { LearningStatus } from '@/lib/models/learning';
import { cn } from '@/lib/utils';

const BRAND = '#1A4D4F';

export interface CourseRowProps {
    title: string;
    description?: string | null;
    organisationName?: string | null;
    organisationLogoUrl?: string | null;
    estimatedDuration?: string | null;
    totalSteps?: number;
    status: LearningStatus;
    locked?: boolean;
    percentage?: number;
    isMandatory?: boolean;
    /** The next actionable module in a program — highlighted with a Fortsätt/Börja CTA. */
    isNext?: boolean;
    actionLabel?: string;
    completedAt?: string;
    onClick?: () => void;
}

export const CourseRow: React.FC<CourseRowProps> = ({
    title, description, organisationName, organisationLogoUrl, estimatedDuration, totalSteps,
    status, locked, percentage, isMandatory, isNext, actionLabel, completedAt, onClick,
}) => {
    const clickable = !!onClick && !locked;
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable}
            className={cn(
                NEXILINK_ROW, 'w-full text-left',
                clickable ? 'cursor-pointer' : 'cursor-default',
                locked && 'opacity-70',
                isNext && 'bg-[#A4C3B2]/15 hover:bg-[#A4C3B2]/25',
            )}
        >
            <CourseAvatar organisationName={organisationName ?? undefined} organisationLogoUrl={organisationLogoUrl ?? undefined} size="sm" />

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <p className={cn('text-sm truncate', status === 'completed' ? 'font-medium text-gray-500' : 'font-semibold text-gray-900')}>
                        {title}
                    </p>
                    {isMandatory && status !== 'completed' && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 flex-shrink-0">Obligatorisk</span>
                    )}
                </div>
                {description && <p className="text-xs text-gray-500 truncate">{description}</p>}
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-0.5">
                    {estimatedDuration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{estimatedDuration}</span>}
                    {!!totalSteps && totalSteps > 0 && <span className="flex items-center gap-1"><Layers className="w-3 h-3" />{totalSteps} steg</span>}
                    {status === 'in_progress' && !!totalSteps && totalSteps > 0 && <span className="font-medium text-[#1A4D4F]">· {percentage}%</span>}
                    {status === 'completed' && completedAt && <span>Slutförd {new Date(completedAt).toLocaleDateString('sv-SE')}</span>}
                </div>
            </div>

            {/* Trailing action / status */}
            {locked ? (
                <span className="flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0"><Lock className="w-3.5 h-3.5" /> Låst</span>
            ) : isNext ? (
                <span
                    className="flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: BRAND }}
                >
                    <Play className="w-3 h-3 fill-white" /> {actionLabel ?? 'Fortsätt'}
                </span>
            ) : (
                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={status} />
                    {clickable && <ChevronRight className="w-4 h-4 text-gray-300" />}
                </div>
            )}
        </button>
    );
};

export default CourseRow;
